/**
 * Money, dates, numbers — formatted in one place, in the viewer's zone.
 *
 * `.docs/00-index.md` §5: "Money/date rendering in the SPA → `useFormat()` over
 * `packages/shared`. No `toLocaleString` in a component." That ban is a lint rule
 * (`eslint.config.js`, the `apps/web` override): `Intl.` and `.toLocaleString` are
 * errors everywhere under `apps/web/src` except this file.
 *
 * The rules it carries, and where each comes from:
 *
 *   - **Money is `en-US`.** `$3,180.00`, `€890.00` — the mockups' spelling, and
 *     `formatMoney`'s own default. The currency is on the `Money`, never on the
 *     locale, so a EUR client sees `€890.00` and not `890,00 €`.
 *   - **Dates are `en-GB`, day first.** `6 Aug 2026`. `packages/shared/src/dates.ts`
 *     pins that locale itself (`DATE_LOCALE`) and trims CLDR's four-letter "Sept"
 *     to three, because all 23 dates in the mockups are three letters.
 *   - **The zone is the browser's**, from `Intl.DateTimeFormat().resolvedOptions()`.
 *     `ARCHITECTURE.md` §Frontend: "The viewer's zone comes from the browser and is
 *     stored on the member for email." The API speaks UTC; this is where an instant
 *     becomes a time of day.
 *
 * A page that wants a different zone — a call slot in the *client's* zone rather
 * than the viewer's — passes it to `date()`. That is the only override, and the
 * mockups have exactly one page that needs it (`client/calls.html`: "Times shown in
 * your zone").
 *
 * ## A time of day always says which clock it is on — session 24, R2-07
 *
 * The zone above stays the browser's, and that is a defensible default only for as
 * long as the value **says so**. It did not: `/app/requests` printed an approval as
 * `12 Aug 2026, 10:47` in whatever zone the reader's laptop was in, on a page whose
 * own copy calls that record the thing that authorises a charge, while a person's
 * Settings now offers them a zone of their own and `/app/calls` overrides this one
 * out loud. A Budapest laptop read New York's 04:47 as 10:47 and nothing on the page
 * disagreed.
 *
 * The rule, and it is about the **label** rather than about the zone, because a
 * stamp in one zone beside a call time in another is worse than either:
 *
 * > **No time of day is rendered in the portal without the zone it is in.** The two
 * > styles that carry one are `clock` (`08:00 EDT`) and `stamp`
 * > (`12 Aug 2026, 10:22 EDT`); `time` (`08:00`) is only for a control whose own
 * > surrounding copy states the zone and passes it explicitly, which is the booking
 * > grid and nothing else.
 *
 * `datetime` is what `stamp` replaced and is **not** for a browser: it survives for
 * the call mails, which are written into a sentence that names their zone
 * (`scheduling/service.ts`). A new `<DateText style="datetime">` in this SPA is
 * almost certainly a `stamp`.
 */

import {
  formatAgo,
  formatDate,
  formatBytes,
  formatMonth,
  formatMoney,
  formatPeriod,
  formatNumber,
  formatFraction,
  formatPercent,
  formatQty,
  parsePeriod,
  type DateStyle,
  type Money,
  type FractionFormatOptions,
  type NumberFormatOptions,
  type QtyFormatOptions,
  type UnitNoun,
} from '@fareast/shared'
import { useMemo } from 'react'

import { now } from '../integrations/clock.js'

/** `formatMoney`'s own default, restated so the one call site is readable. */
const MONEY_LOCALE = 'en-US'

/**
 * The zone the browser is in.
 *
 * Read once per hook rather than once per module: a test moves it with
 * `vi.stubGlobal`, and a module-level constant would freeze whatever the first
 * import saw.
 */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export type Formatter = {
  /** `$3,180.00`. `showCents: 'auto'` drops `.00` on a whole amount. */
  money: (value: Money, options?: { showCents?: 'always' | 'auto' }) => string
  /** `6 Aug 2026` by default; `zone` overrides the viewer's. */
  date: (instant: Date | string, style?: DateStyle, zone?: string) => string
  /**
   * A `DateOnly` as a date: `2026-08-26` → `26 Aug 2026`.
   *
   * A day is not an instant, and rendering one through `date()` reads it in the
   * viewer's zone — which moves `2026-08-26` to the 25th for anybody west of
   * Greenwich. This reads it at noon UTC, which no zone can carry into another day.
   * Every invoice due date, content drop and milestone date in the portal is one of
   * these, and it was written out three times before it was a function.
   */
  day: (value: string, style?: DateStyle) => string
  /**
   * A period as a short month: `2026-08` → `Aug 2026`.
   *
   * A month is not an instant, so it does not go through `date()` and is not in the
   * viewer's zone — `packages/shared/src/dates.ts` `formatMonth` says why at length.
   * Session 19's `PeriodSeg` and every "as of" caption print through this.
   */
  month: (period: string) => string
  /** A period as a long month: `2026-08` → `August 2026`. What a title prints. */
  monthLong: (period: string) => string
  /** `1,234.5`. Two fraction digits at most unless pinned. */
  number: (value: number, options?: NumberFormatOptions) => string
  /** `6.0 h`, `3 channels` — a number and its unit noun, pluralised. */
  qty: (value: number, unit: UnitNoun, options?: QtyFormatOptions) => string
  /** Basis points as a percentage: `1500` → `15%`. */
  percent: (bps: number) => string
  /**
   * A fraction of one as a percentage: `0.801` → `80.1%`.
   *
   * The read models' own shape — every `share` and `ratio` on
   * `schemas/readmodels/analytics.ts` is a fraction, not basis points, and `percent`
   * above refuses one. `sign: false` gives the figure alone, for `HeroFigure`'s
   * superscript.
   */
  fraction: (value: number, options?: FractionFormatOptions) => string
  /** A file size: `3755840000` → `3.8 GB`. Decimal units, as a file manager shows. */
  bytes: (value: number) => string
  /**
   * A UTC offset in minutes, as a label: `180` → `UTC+3`, `-240` → `UTC−4`.
   *
   * `BookingRulesView` carries the two offsets in **minutes** rather than as a
   * string, because the sign, the glyph and whether to print minutes are the SPA's
   * (`CLAUDE.md` §Gotchas) — a page handed a string could not print `UTC+05:45`.
   * This is that rendering, once: a true minus sign rather than a hyphen, and the
   * minutes only when there are any.
   */
  offset: (minutes: number) => string
  /**
   * How long ago an instant was: `3 hours ago`, `yesterday`, `5 days ago`.
   *
   * Session 46's, for the forms inbox, where a lead is read by how fresh it is. Read
   * against `integrations/clock.ts` at the moment of rendering, so a test moves it with
   * the clock it already pins. Never on its own: the absolute date stands beside it,
   * because the coarse end of a relative date is approximate by nature.
   */
  ago: (instant: Date | string) => string
  /** The zone every `date()` above used. For a caption that has to say so. */
  zone: string
}

export function useFormat(): Formatter {
  const zone = viewerTimeZone()

  return useMemo<Formatter>(
    () => ({
      money: (value, options) => formatMoney(value, MONEY_LOCALE, options ?? {}),
      date: (instant, style = 'short', override) =>
        formatDate(
          typeof instant === 'string' ? new Date(instant) : instant,
          override ?? zone,
          style,
        ),
      day: (value, style = 'short') => formatDate(new Date(`${value}T12:00:00.000Z`), 'UTC', style),
      month: (value) => formatMonth(parsePeriod(value)),
      monthLong: (value) => formatPeriod(parsePeriod(value)),
      number: (value, options) => formatNumber(value, MONEY_LOCALE, options ?? {}),
      qty: (value, unit, options) => formatQty(value, unit, options ?? {}),
      percent: (bps) => formatPercent(bps, MONEY_LOCALE),
      fraction: (value, options) => formatFraction(value, { ...options, locale: MONEY_LOCALE }),
      bytes: (value) => formatBytes(value, MONEY_LOCALE),
      offset: utcOffsetLabel,
      ago: (instant) => formatAgo(typeof instant === 'string' ? new Date(instant) : instant, now()),
      zone,
    }),
    [zone],
  )
}

/**
 * `180` → `UTC+3`; `-240` → `UTC−4`; `345` → `UTC+05:45`.
 *
 * Outside the hook because it is arithmetic on an integer rather than a format: no
 * locale, no zone, no `Intl`. Kathmandu is the case the minutes exist for.
 */
export function utcOffsetLabel(minutes: number): string {
  const sign = minutes < 0 ? '−' : '+'
  const total = minutes < 0 ? -minutes : minutes
  const hours = (total - (total % 60)) / 60
  const rest = total % 60
  return rest === 0
    ? `UTC${sign}${String(hours)}`
    : `UTC${sign}${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}
